/**
 * A brand-new card: Vertical orientation, no footer, no application
 * references, and exactly the four default-enabled fields — Primary Image,
 * Product Title / Name, List Price and Primary CTA Button (spec R22, R31, R36).
 *
 * `now` and `id` are supplied by the caller so this stays clock-free and
 * deterministic; the repository adapter owns both.
 */

import {
  DEFAULT_ENABLED_FIELD_IDS,
  DEFAULT_FIELD_ORDER,
  getDefaultFieldConfiguration,
} from '../constants';
import { FieldConfiguration, FieldId, ProductCard } from '../types';
import { normalizeCard } from './normalize';

// The starter fields carry their configuration explicitly, exactly as TOGGLE_FIELD
// does for a field enabled later (productCardReducer): every enabled field has an
// entry, so a consumer reads the document without owning a defaults table. Readers
// must still fall back on absent entries — that leniency is the schemaVersion-1
// additive policy, not an invitation to write sparsely.
const starterConfiguration = (): Partial<Record<FieldId, FieldConfiguration>> => {
  const configuration: Partial<Record<FieldId, FieldConfiguration>> = {};
  DEFAULT_ENABLED_FIELD_IDS.forEach((fieldId) => {
    configuration[fieldId] = getDefaultFieldConfiguration(fieldId);
  });
  return configuration;
};

export const createDefaultCard = (name: string, now: string, id: string): ProductCard =>
  normalizeCard({
    id,
    name,
    orientation: 'vertical',
    footerEnabled: false,
    fieldOrder: [...DEFAULT_FIELD_ORDER],
    enabledFieldIds: [...DEFAULT_ENABLED_FIELD_IDS],
    placements: [],
    configuration: starterConfiguration(),
    references: [],
    updatedAt: now,
  });
